import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  useFilters,
  IFilterState,
  IFilterActions,
} from '../../../components/DealerPage/hooks/useFilters';
import {
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPageFilter,
} from '../../../api/types/searchPageApiTypes';
import { ResponseMakeModelType, ResponseModelFilter } from '../../../types';
import { useLocationContext } from '../../../features/location/Location.context';
import {
  IFilterMakeModelActions,
  IFilterMakeModelState,
  useFiltersMakeModel,
} from '../../../components/DealerPage/hooks/useFiltersMakeModel';

export type DealerFilterStateType = IFilterState &
  IFilterMakeModelState & {
    appliedFiltersRangeCount: number;
    isFilterModalOpen: boolean;
    isAddAnotherEnabled: boolean;
  };
export type DealerFilterDispatchType = IFilterActions &
  IFilterMakeModelActions & {
    setIsFilterModalOpen: (isOpen: boolean) => void;
    getCountOfFiltersApplied: (search: string, sort: string) => number;
    reset: () => void;
  };

export const DealerFilterDispatchContext = createContext(
  {} as DealerFilterDispatchType,
);

export const DealerFilterStateContext = createContext(
  {} as DealerFilterStateType,
);

export const useDealerFilterDispatch = (): DealerFilterDispatchType =>
  useContext(DealerFilterDispatchContext);
export const useDealerFilterState = (): DealerFilterStateType =>
  useContext(DealerFilterStateContext);

export const DealerFilterProvider = ({
  filtersData,
  filtersMakeModelData,
  filtersQueryParams,
  makeModelParams,
  rangeQueryParams,
  children,
}: {
  filtersData: ISearchPageFilter[];
  filtersMakeModelData: ResponseModelFilter;
  filtersQueryParams: IGetAdsRequestFilter[];
  makeModelParams: ResponseMakeModelType;
  rangeQueryParams: IGetAdsRequestRange[];
  children: ReactElement;
}): ReactElement => {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const { state: stateFilters, dispatch: dispatchFilters } = useFilters(
    filtersData,
    filtersQueryParams,
    rangeQueryParams,
  );

  const { state: stateMakeModel, dispatch: dispatchMakeModel } =
    useFiltersMakeModel(filtersData, filtersMakeModelData, makeModelParams);

  const { selectedCounty, resetToDefault } = useLocationContext();

  const resetFilters = useCallback(() => {
    dispatchFilters.resetFilters();
    dispatchMakeModel.resetMakeModel();
    resetToDefault();
    // TODO: Clean up this callback's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCountOfFiltersApplied = useCallback(
    (search: string, sort: string) => {
      let count = 0;
      const makeModelCount = stateMakeModel.makeModel.filter(
        (makeModelGroup) =>
          makeModelGroup.makeValue.length > 0 &&
          makeModelGroup.makeValue !== 'All Makes' &&
          makeModelGroup.makeValue !== '',
      ).length;
      const locationCount = selectedCounty?.value ? 1 : 0;

      count += locationCount;
      count += stateFilters.appliedFiltersRangeCount;
      count += makeModelCount;
      count += search ? 1 : 0;
      count += sort ? 1 : 0;
      return count;
    },
    [
      stateFilters.appliedFiltersRangeCount,
      stateMakeModel.makeModel,
      selectedCounty,
    ],
  );

  return (
    <DealerFilterDispatchContext.Provider
      value={{
        setIsFilterModalOpen,
        getCountOfFiltersApplied,
        ...dispatchFilters,
        ...dispatchMakeModel,
        reset: resetFilters,
      }}
    >
      <DealerFilterStateContext.Provider
        value={{
          ...stateFilters,
          ...stateMakeModel,
          isFilterModalOpen,
        }}
      >
        {children}
      </DealerFilterStateContext.Provider>
    </DealerFilterDispatchContext.Provider>
  );
};
